import type { CustomMenuItem, SortMenuData } from '@/types/carrier-types';
import { Menu } from 'primereact/menu';
import type { MenuItem } from 'primereact/menuitem';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Flag } from './Flag';
import { Button } from 'primereact/button';
import { cn } from '@/helpers/utils/functionality-utils';

export const LanguageMenu = ({ isMobileMenu }: { isMobileMenu?: boolean }) => {
  const { t: translate } = useTranslation('common');
  const menuLeft = useRef<Menu>(null);
  const { i18n } = useTranslation();

  const savedLang = localStorage.getItem('i18nextLng');
  const [selectedLanguage, setSelectedLanguage] = useState<string>(
    savedLang || 'en',
  );

  const optionTemplate = (option: CustomMenuItem) => {
    const { countryCode } = option.data as SortMenuData & {
      countryCode: string;
    };
    const icon = option.icon;
    const isJp = countryCode?.toLowerCase() === 'jp';
    return (
      <div
        role="menuitem"
        aria-label={String(option.label)}
        className="p-menuitem-content relative flex items-center justify-between gap-2 pr-3"
      >
        <div className="p-menuitem-link overflow-visible">
          <span className={'p-menuitem-icon'} />
          {icon && <span className={'p-menuitem-icon ' + icon}></span>}
          <div className="relative">
            {isJp && (
              <span className="absolute -right-7 -top-2 z-[99999] text-xs font-semibold uppercase text-red-500">
                {translate('beta')}
              </span>
            )}
            {String(option.label)}
          </div>
        </div>
        <Flag code={countryCode} />
      </div>
    );
  };

  const items: MenuItem[] = [
    {
      data: { countryCode: 'UK' },
      label: 'English',
      icon:
        selectedLanguage === 'en' ? 'pi pi-check' : ' pi pi-check invisible',
      template: optionTemplate,
      command: () => {
        i18n.changeLanguage('en');
        setSelectedLanguage('en');
      },
    },
    {
      data: { countryCode: 'JP' },
      label: '日本語',
      icon: selectedLanguage === 'ja' ? 'pi pi-check' : 'pi pi-check invisible',
      command: () => {
        i18n.changeLanguage('ja');
        setSelectedLanguage('ja');
      },
      template: optionTemplate,
    },
  ];

  return isMobileMenu ? (
    <div className="flex flex-col gap-3 p-4 *:shadow-none">
      <Button
        type="button"
        onClick={() => {
          i18n.changeLanguage('en');
          setSelectedLanguage('en');
        }}
        text
        severity="secondary"
        className="flex items-center justify-between gap-2 shadow-none"
      >
        <div className="flex items-center gap-2">
          <i
            className={cn(
              'pi pi-check',
              selectedLanguage !== 'en' && 'invisible',
            )}
          />
          English
        </div>
        <Flag code="UK" />
      </Button>
      <Button
        type="button"
        onClick={() => {
          i18n.changeLanguage('ja');
          setSelectedLanguage('ja');
        }}
        text
        severity="secondary"
        className="flex items-center justify-between gap-2 shadow-none"
      >
        <div className="flex items-center gap-2">
          <i
            className={cn(
              'pi pi-check',
              selectedLanguage !== 'ja' && 'invisible',
            )}
          />
          日本語
        </div>
        <Flag code="JP" />
      </Button>
    </div>
  ) : (
    <>
      <button
        title={translate('Language')}
        type="button"
        className="p-link layout-topbar-button"
        onClick={(event) => menuLeft?.current?.toggle(event)}
        aria-controls="popup_menu_left"
        aria-haspopup
      >
        <div className="pi pi-globe mr-2 text-2xl sm:mr-1" />
        <span className="ml-2">{translate('Language')}</span>
      </button>
      <Menu model={items} popup ref={menuLeft} id="popup_menu_left" />
    </>
  );
};
